'use client'

import { trackSessionStart } from '@/lib/analytics'
import { DEFAULT_SLIDER_ASPECT_RATIO, type TileAspectRatio } from '@/components/scenario/aspectRatio'
import { DEFAULT_THUMBNAIL_ROUNDED } from '@/components/scenario/thumbnailCorner'
import { DEFAULT_EMBED_FONT, type EmbedFont } from '@/components/scenario/embedFont'
import { useViewportVideoPrefetch } from '@/hooks/useViewportVideoPrefetch'
import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { VideoModal } from '../modal'
import { useShadowHostPortal } from '../shadow-host-portal/ShadowHostPortalContext'
import PCSliderGrid from './PCSliderGrid'
import SPSliderCarousel from './SPSliderCarousel'
import { useResponsiveBreakpoint } from './useResponsiveBreakpoint'
import { useSliderNavigation } from './useSliderNavigation'

export interface SliderPlayerProps {
  nodes: ScenarioNodeWithVideoUrl[]
  videoOrder: string[]
  aspectRatio?: TileAspectRatio
  /** サムネイルの角丸。false なら直角（先方サイトのデザインに合わせる設定） */
  thumbnailRounded?: boolean
  /** 視聴モーダルのフォント（プレイリスト単位の設定） */
  embedFont?: EmbedFont
  scenarioId?: string
  /** プレイリスト共通CTA（動画CTA未設定時の既定値・ADR-0019） */
  ctaUrl?: string | null
  ctaLabel?: string | null
}

const SliderPlayer: FC<SliderPlayerProps> = ({
  nodes,
  videoOrder,
  aspectRatio = DEFAULT_SLIDER_ASPECT_RATIO,
  thumbnailRounded = DEFAULT_THUMBNAIL_ROUNDED,
  embedFont = DEFAULT_EMBED_FONT,
  scenarioId,
  ctaUrl,
  ctaLabel,
}) => {
  const spScrollRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasTrackedRef = useRef(false)
  const [isPiPActive, setIsPiPActive] = useState(false)

  const orderedNodes = useMemo(
    () =>
      videoOrder && videoOrder.length > 0
        ? videoOrder
            .map((videoId) => nodes.find((node) => node.videoId === videoId))
            .filter((node): node is ScenarioNodeWithVideoUrl => node !== undefined)
        : nodes,
    [videoOrder, nodes],
  )

  const { isSP } = useResponsiveBreakpoint()
  useViewportVideoPrefetch(containerRef, orderedNodes[0]?.videoUrl, isSP)

  const {
    currentIndex,
    isTransitioning,
    slideDirection,
    selectedVideoIndex,
    visibleNodes,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    handleSPThumbnailClick,
    handleCloseModal,
    handleRequestOpen,
  } = useSliderNavigation({ orderedNodes })

  // A7: Center first card on SP mount
  useEffect(() => {
    if (isSP && spScrollRef.current) {
      const container = spScrollRef.current
      // Scroll to center the first item
      container.scrollLeft = 0
    }
  }, [isSP])

  // Track SESSION_START when slider becomes visible
  useEffect(() => {
    if (!scenarioId || hasTrackedRef.current) return
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !hasTrackedRef.current) {
          hasTrackedRef.current = true
          trackSessionStart(scenarioId)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [scenarioId])

  // モーダル展開中は shadowHost を body 直下へ portal して、先方サイトの
  // stacking context（z-index 競合や祖先の position: relative）を回避する。
  const portal = useShadowHostPortal()
  const isOverlayActive = selectedVideoIndex !== null || isPiPActive
  useEffect(() => {
    portal?.setPortalActive(isOverlayActive)
  }, [isOverlayActive, portal])
  useEffect(() => {
    return () => {
      portal?.setPortalActive(false)
    }
  }, [portal])

  if (orderedNodes.length === 0) {
    return (
      <div className="w-full max-w-screen-xl mx-auto">
        <div className="text-center py-10 text-red-500 text-[16px]">動画が見つかりません</div>
      </div>
    )
  }

  return (
    <>
      {/*
        モーダル展開中はスライダー UI を visibility: hidden で見えなくする。
        PR #95 で shadowHost を body 直下へ portal するようになった結果、
        shadowHost 内の React tree（スライダー + モーダル）が一緒に最前面に出るため、
        backdrop の半透明（rgba(0,0,0,0.75)）越しにスライダーが透けて見えてしまう問題への対応。
        TilePlayer.tsx と同じ方針（visibility: hidden、display: none ではない）。
      */}
      <div
        ref={containerRef}
        className={`w-full max-w-screen-xl mx-auto px-5 pt-5 pb-1 ${
          isOverlayActive ? 'invisible' : ''
        }`}
      >
        {/* A7: SP Carousel view */}
        {isSP ? (
          <SPSliderCarousel
            orderedNodes={orderedNodes}
            spScrollRef={spScrollRef}
            onThumbnailClick={handleSPThumbnailClick}
            aspectRatio={aspectRatio}
            thumbnailRounded={thumbnailRounded}
          />
        ) : (
          /* PC: Original grid view */
          <PCSliderGrid
            orderedNodes={orderedNodes}
            visibleNodes={visibleNodes}
            currentIndex={currentIndex}
            isTransitioning={isTransitioning}
            slideDirection={slideDirection}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onThumbnailClick={handleThumbnailClick}
            aspectRatio={aspectRatio}
            thumbnailRounded={thumbnailRounded}
          />
        )}
      </div>

      <VideoModal
        nodes={nodes}
        videoOrder={videoOrder}
        initialVideoIndex={selectedVideoIndex ?? 0}
        isOpen={selectedVideoIndex !== null}
        onClose={handleCloseModal}
        onRequestOpen={handleRequestOpen}
        onPiPActiveChange={setIsPiPActive}
        scenarioId={scenarioId}
        ctaUrl={ctaUrl}
        ctaLabel={ctaLabel}
        embedFont={embedFont}
      />
    </>
  )
}

export default SliderPlayer
